import React, { useRef, useEffect, useState } from 'react';

export default function GlowingCard({
  children,
  className = '',
  glowColor = 'blue',
  hoverEffect = true,
  borderGlow = true,
}) {
  const cardRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const glowColors = {
    blue: {
      primary: 'rgba(59, 130, 246, 0.4)',
      secondary: 'rgba(59, 130, 246, 0.1)',
      border: 'rgba(59, 130, 246, 0.3)',
    },
    purple: {
      primary: 'rgba(139, 92, 246, 0.4)',
      secondary: 'rgba(139, 92, 246, 0.1)',
      border: 'rgba(139, 92, 246, 0.3)',
    },
    cyan: {
      primary: 'rgba(6, 182, 212, 0.4)',
      secondary: 'rgba(6, 182, 212, 0.1)',
      border: 'rgba(6, 182, 212, 0.3)',
    },
    green: {
      primary: 'rgba(16, 185, 129, 0.4)',
      secondary: 'rgba(16, 185, 129, 0.1)',
      border: 'rgba(16, 185, 129, 0.3)',
    },
    gold: {
      primary: 'rgba(245, 158, 11, 0.4)',
      secondary: 'rgba(245, 158, 11, 0.1)',
      border: 'rgba(245, 158, 11, 0.3)',
    },
  };

  const colors = glowColors[glowColor] || glowColors.blue;

  useEffect(() => {
    const card = cardRef.current;
    if (!card || !hoverEffect) return;

    const handleMouseMove = (e) => {
      const rect = card.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    };

    card.addEventListener('mousemove', handleMouseMove);
    return () => card.removeEventListener('mousemove', handleMouseMove);
  }, [hoverEffect]);

  return (
    <div
      ref={cardRef}
      className={`relative group ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        transform: isHovered && hoverEffect ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'transform 0.3s ease',
      }}
    >
      {/* Border glow effect */}
      {borderGlow && (
        <div
          className="absolute -inset-px rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `linear-gradient(135deg, ${colors.border}, transparent, ${colors.border})`,
          }}
        />
      )}

      {/* Card content */}
      <div className="relative bg-slate-900/80 backdrop-blur-sm rounded-xl border border-slate-800/50 overflow-hidden">
        {/* Mouse follower glow */}
        {isHovered && hoverEffect && (
          <div
            className="absolute pointer-events-none transition-opacity duration-300"
            style={{
              left: mousePosition.x - 150,
              top: mousePosition.y - 150,
              width: 300,
              height: 300,
              background: `radial-gradient(circle, ${colors.primary} 0%, transparent 70%)`,
              opacity: 0.6,
            }}
          />
        )}

        {/* Content */}
        <div className="relative z-10">
          {children}
        </div>

        {/* Bottom glow line */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `linear-gradient(90deg, transparent, ${colors.border}, transparent)`,
          }}
        />
      </div>

      {/* External glow on hover */}
      {isHovered && hoverEffect && (
        <div
          className="absolute inset-0 -z-10 rounded-xl blur-xl transition-opacity duration-500"
          style={{
            background: colors.secondary,
            opacity: 0.5,
          }}
        />
      )}
    </div>
  );
}
